import {
  SignalCategorySchema,
  type SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';

export const WIZ_DOCUMENTED_ISSUE_TYPES = [
  'CLOUD_CONFIGURATION',
  'THREAT_DETECTION',
  'TOXIC_COMBINATION',
] as const;

export type WizIssueType = (typeof WIZ_DOCUMENTED_ISSUE_TYPES)[number];

export const WIZ_DOCUMENTED_SOURCE_RULE_TYPES = [
  'Control',
  'CloudConfigurationRule',
  'CloudEventRule',
] as const;

export type WizSourceRuleType =
  (typeof WIZ_DOCUMENTED_SOURCE_RULE_TYPES)[number];

export const WIZ_DOCUMENTED_RISKS = [
  'VULNERABILITY',
  'INSECURE_KUBERNETES_CLUSTER',
] as const;

export type WizRisk = (typeof WIZ_DOCUMENTED_RISKS)[number];

type WizTaxonomyCategoryMapping = {
  signalCategory: SignalCategoryType;
  risks?: readonly string[];
  subcategories?: readonly string[];
  issueTypes?: readonly string[];
};

type WizTaxonomyLookup = {
  riskToCategory: Record<string, SignalCategoryType>;
  subcategoryToCategory: Record<string, SignalCategoryType>;
  issueTypeToCategory: Record<string, SignalCategoryType>;
};

type WizTaxonomyInput = {
  issueType?: string;
  risks?: string[];
  subcategories?: string[];
};

type WizTextInferenceInput = {
  texts: Array<string | null | undefined>;
};

const normalizeTaxonomyValue = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

const createTaxonomyLookup = (
  mappings: readonly WizTaxonomyCategoryMapping[],
): WizTaxonomyLookup => {
  const riskToCategory: Record<string, SignalCategoryType> = {};
  const subcategoryToCategory: Record<string, SignalCategoryType> = {};
  const issueTypeToCategory: Record<string, SignalCategoryType> = {};

  mappings.forEach(({ signalCategory, risks, subcategories, issueTypes }) => {
    risks?.forEach((risk) => {
      riskToCategory[normalizeTaxonomyValue(risk)] = signalCategory;
    });
    subcategories?.forEach((subcategory) => {
      subcategoryToCategory[normalizeTaxonomyValue(subcategory)] =
        signalCategory;
    });
    issueTypes?.forEach((issueType) => {
      issueTypeToCategory[normalizeTaxonomyValue(issueType)] = signalCategory;
    });
  });

  return {
    riskToCategory,
    subcategoryToCategory,
    issueTypeToCategory,
  };
};

/**
 * Broader Wiz taxonomy matrix:
 * - prefers explicit subcategory classification when available
 * - then falls back to risk, then issue type
 * - if no deterministic match exists, caller should use AI classification
 *   and finally fallback to SignalCategory.OTHER
 */
export const WIZ_TAXONOMY_CATEGORY_MAPPINGS: WizTaxonomyCategoryMapping[] = [
  {
    signalCategory: SignalCategorySchema.enum.SERVER_MISCONFIGURATION,
    issueTypes: ['CLOUD_CONFIGURATION'],
    subcategories: [
      'MISCONFIGURATION',
      'SECURITY_MISCONFIGURATION',
      'SERVER_MISCONFIGURATION',
    ],
  },
  {
    signalCategory: SignalCategorySchema.enum.BROKEN_AUTHENTICATION,
    subcategories: [
      'BROKEN_AUTHENTICATION',
      'WEAK_AUTHENTICATION',
      'AUTHENTICATION_BYPASS',
      'JWT',
      'JWT_MISCONFIGURATION',
    ],
  },
  {
    signalCategory: SignalCategorySchema.enum.BROKEN_ACCESS,
    subcategories: ['BROKEN_ACCESS', 'BROKEN_ACCESS_CONTROL', 'AUTHORIZATION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.IDOR,
    subcategories: ['IDOR', 'INSECURE_DIRECT_OBJECT_REFERENCE'],
  },
  {
    signalCategory: SignalCategorySchema.enum.BOLA,
    subcategories: ['BOLA'],
  },
  {
    signalCategory: SignalCategorySchema.enum.BOPLA,
    subcategories: ['BOPLA'],
  },
  {
    signalCategory: SignalCategorySchema.enum.BFLA,
    subcategories: ['BFLA', 'BOFLA'],
  },
  {
    signalCategory: SignalCategorySchema.enum.SQL_INJECTION,
    subcategories: ['SQL_INJECTION', 'SQLI'],
  },
  {
    signalCategory: SignalCategorySchema.enum.NOSQL_INJECTION,
    subcategories: ['NOSQL_INJECTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.LDAP_INJECTION,
    subcategories: ['LDAP_INJECTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.XPATH_INJECTION,
    subcategories: ['XPATH_INJECTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.CSV_INJECTION,
    subcategories: ['CSV_INJECTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.COMMAND_INJECTION,
    subcategories: ['COMMAND_INJECTION', 'RCE', 'REMOTE_CODE_EXECUTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.CODE_INJECTION,
    subcategories: ['CODE_INJECTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.SSTI,
    subcategories: ['SSTI', 'SERVER_SIDE_TEMPLATE_INJECTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.REFLECTED_XSS,
    subcategories: ['REFLECTED_XSS', 'XSS', 'CROSS_SITE_SCRIPTING'],
  },
  {
    signalCategory: SignalCategorySchema.enum.STORED_XSS,
    subcategories: ['STORED_XSS'],
  },
  {
    signalCategory: SignalCategorySchema.enum.DOM_XSS,
    subcategories: ['DOM_XSS'],
  },
  {
    signalCategory: SignalCategorySchema.enum.CSRF,
    subcategories: ['CSRF', 'CROSS_SITE_REQUEST_FORGERY'],
  },
  {
    signalCategory: SignalCategorySchema.enum.SSRF,
    subcategories: ['SSRF', 'SERVER_SIDE_REQUEST_FORGERY'],
  },
  {
    signalCategory: SignalCategorySchema.enum.XXE,
    subcategories: ['XXE', 'XML_EXTERNAL_ENTITY'],
  },
  {
    signalCategory: SignalCategorySchema.enum.OPEN_REDIRECT,
    subcategories: ['OPEN_REDIRECT'],
  },
  {
    signalCategory: SignalCategorySchema.enum.PATH_TRAVERSAL,
    subcategories: ['PATH_TRAVERSAL', 'DIRECTORY_TRAVERSAL'],
  },
  {
    signalCategory: SignalCategorySchema.enum.FILE_UPLOAD,
    subcategories: ['FILE_UPLOAD', 'UNRESTRICTED_FILE_UPLOAD'],
  },
  {
    signalCategory: SignalCategorySchema.enum.FILE_INCLUSION,
    subcategories: ['FILE_INCLUSION', 'LFI', 'RFI'],
  },
  {
    signalCategory: SignalCategorySchema.enum.ARBITRARY_FILE_READ,
    subcategories: ['ARBITRARY_FILE_READ'],
  },
  {
    signalCategory: SignalCategorySchema.enum.ARBITRARY_FILE_WRITE,
    subcategories: ['ARBITRARY_FILE_WRITE'],
  },
  {
    signalCategory: SignalCategorySchema.enum.WEAK_CRYPTO,
    subcategories: ['WEAK_CRYPTO', 'CRYPTOGRAPHIC_FAILURE'],
  },
  {
    signalCategory: SignalCategorySchema.enum.INSECURE_DESERIALIZATION,
    subcategories: ['INSECURE_DESERIALIZATION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.PARAMETER_POLLUTION,
    subcategories: ['PARAMETER_POLLUTION', 'HTTP_PARAMETER_POLLUTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.HTTP_REQUEST_SMUGGLING,
    subcategories: ['HTTP_REQUEST_SMUGGLING'],
  },
  {
    signalCategory: SignalCategorySchema.enum.HTTP_RESPONSE_SPLITTING,
    subcategories: ['HTTP_RESPONSE_SPLITTING'],
  },
  {
    signalCategory: SignalCategorySchema.enum.SESSION_FIXATION,
    subcategories: ['SESSION_FIXATION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.MASS_ASSIGNMENT,
    subcategories: ['MASS_ASSIGNMENT'],
  },
  {
    signalCategory: SignalCategorySchema.enum.INSECURE_RANDOMNESS,
    subcategories: ['INSECURE_RANDOMNESS'],
  },
  {
    signalCategory: SignalCategorySchema.enum.INFORMATION_DISCLOSURE,
    subcategories: ['INFORMATION_DISCLOSURE', 'SENSITIVE_DATA_EXPOSURE'],
  },
  {
    signalCategory: SignalCategorySchema.enum.DATA_EXPOSURE,
    subcategories: ['DATA_EXPOSURE', 'EXPOSED_DATA'],
  },
  {
    signalCategory: SignalCategorySchema.enum.PROMPT_INJECTION,
    subcategories: ['PROMPT_INJECTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.PROTOTYPE_POLLUTION,
    subcategories: ['PROTOTYPE_POLLUTION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.PADDING_ORACLE,
    subcategories: ['PADDING_ORACLE'],
  },
  {
    signalCategory: SignalCategorySchema.enum.RACE_CONDITION,
    subcategories: ['RACE_CONDITION'],
  },
  {
    signalCategory: SignalCategorySchema.enum.CVE,
    subcategories: ['CVE'],
  },
  {
    signalCategory: SignalCategorySchema.enum.OTHER,
    risks: ['VULNERABILITY', 'THREAT_DETECTION', 'TOXIC_COMBINATION'],
    issueTypes: ['THREAT_DETECTION', 'TOXIC_COMBINATION'],
  },
];

const WIZ_TAXONOMY_LOOKUP = createTaxonomyLookup(
  WIZ_TAXONOMY_CATEGORY_MAPPINGS,
);

const findFirstCategory = ({
  values,
  lookup,
}: {
  values?: string[];
  lookup: Record<string, SignalCategoryType>;
}): SignalCategoryType | undefined => {
  if (!values || values.length === 0) {
    return undefined;
  }

  return values
    .map((value) => lookup[normalizeTaxonomyValue(value)])
    .find((category): category is SignalCategoryType => !!category);
};

export const WIZ_ISSUE_TYPE_TO_SIGNAL_CATEGORY: Partial<
  Record<WizIssueType, SignalCategoryType>
> = Object.fromEntries(
  WIZ_DOCUMENTED_ISSUE_TYPES.flatMap((issueType) => {
    const category = WIZ_TAXONOMY_LOOKUP.issueTypeToCategory[issueType];

    return category ? [[issueType, category]] : [];
  }),
) as Partial<Record<WizIssueType, SignalCategoryType>>;

export const WIZ_RISK_TO_SIGNAL_CATEGORY: Partial<
  Record<WizRisk, SignalCategoryType>
> = Object.fromEntries(
  WIZ_DOCUMENTED_RISKS.flatMap((risk) => {
    const category = WIZ_TAXONOMY_LOOKUP.riskToCategory[risk];

    return category ? [[risk, category]] : [];
  }),
) as Partial<Record<WizRisk, SignalCategoryType>>;

export const mapWizTaxonomyToSignalCategory = ({
  issueType,
  risks,
  subcategories,
}: WizTaxonomyInput): SignalCategoryType | undefined => {
  const subcategoryCategory = findFirstCategory({
    values: subcategories,
    lookup: WIZ_TAXONOMY_LOOKUP.subcategoryToCategory,
  });

  if (subcategoryCategory) {
    return subcategoryCategory;
  }

  const riskCategory = findFirstCategory({
    values: risks,
    lookup: WIZ_TAXONOMY_LOOKUP.riskToCategory,
  });

  if (riskCategory) {
    return riskCategory;
  }

  const issueTypeCategory = issueType
    ? WIZ_TAXONOMY_LOOKUP.issueTypeToCategory[normalizeTaxonomyValue(issueType)]
    : undefined;

  if (issueTypeCategory) {
    return issueTypeCategory;
  }

  return undefined;
};

const WIZ_TEXT_TO_CATEGORY_PATTERNS: Array<{
  category: SignalCategoryType;
  patterns: RegExp[];
}> = [
  {
    category: SignalCategorySchema.enum.SQL_INJECTION,
    patterns: [
      /\bsql\s*injection\b/i,
      /\bsqli\b/i,
      /\bparameteri[sz]ed query\b/i,
    ],
  },
  {
    category: SignalCategorySchema.enum.REFLECTED_XSS,
    patterns: [/\bcross[\s-]*site scripting\b/i, /\bxss\b/i],
  },
  {
    category: SignalCategorySchema.enum.BROKEN_ACCESS,
    patterns: [
      /\bbroken access\b/i,
      /\bauthori[sz]ation bypass\b/i,
      /\bbroken access control\b/i,
    ],
  },
  {
    category: SignalCategorySchema.enum.BROKEN_AUTHENTICATION,
    patterns: [/\bbroken authentication\b/i, /\bauthentication bypass\b/i],
  },
  {
    category: SignalCategorySchema.enum.SSRF,
    patterns: [/\bssrf\b/i, /\bserver[\s-]*side request forgery\b/i],
  },
  {
    category: SignalCategorySchema.enum.CSRF,
    patterns: [/\bcsrf\b/i, /\bcross[\s-]*site request forgery\b/i],
  },
  {
    category: SignalCategorySchema.enum.OPEN_REDIRECT,
    patterns: [/\bopen redirect\b/i],
  },
  {
    category: SignalCategorySchema.enum.PATH_TRAVERSAL,
    patterns: [/\bpath traversal\b/i, /\bdirectory traversal\b/i],
  },
  {
    category: SignalCategorySchema.enum.FILE_UPLOAD,
    patterns: [/\bfile upload\b/i, /\bunrestricted file upload\b/i],
  },
  {
    category: SignalCategorySchema.enum.SERVER_MISCONFIGURATION,
    patterns: [/\bmisconfiguration\b/i, /\binternet[-\s]*exposed\b/i],
  },
  {
    category: SignalCategorySchema.enum.INFORMATION_DISCLOSURE,
    patterns: [/\binformation disclosure\b/i, /\bsensitive data exposure\b/i],
  },
];

export const inferWizTextToSignalCategory = ({
  texts,
}: WizTextInferenceInput): SignalCategoryType | undefined => {
  const normalizedText = texts
    .filter(
      (text): text is string =>
        typeof text === 'string' && text.trim().length > 0,
    )
    .join('\n');

  if (!normalizedText) {
    return undefined;
  }

  const match = WIZ_TEXT_TO_CATEGORY_PATTERNS.find(({ patterns }) =>
    patterns.some((pattern) => pattern.test(normalizedText)),
  );

  return match?.category;
};
